import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { OrderDirection } from 'webapp/enums/enums';
import { uniq } from 'webapp/utils/common';
import type { SearchProps } from 'webapp/utils/filter';
import { isVerified } from 'webapp/utils/verification';

import type Board from 'webapp/models-custom/board';
import BoardLoop from 'webapp/models-custom/board-loop';
import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';

export const viewSettingsNamePrefix = 'popupSelectDataboardsViewSettings';
export const filterSettingsNamePrefix = 'popupSelectDataboardsFilterSettings';

enum DataboardCategory {
	FAVORITE = 'favorite',
	VERIFIED = 'verified',
	LOOPED = 'looped',
}

export default class FilterSortBoardAtSelectPopup extends FilterSort<Board> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;

	@tracked override sortProp = 'order';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.ASC;

	isAnyLoopedBoardAvailable = false;

	override searchProps: SearchProps = {
		name: 1,
	};

	// Use this only to save selected filters in modal until its destroyed (resetFilterSettings used in boards/popup-select to reset filters)
	// Case: Use selected filters when switching between views grid/table then reset it for new modal "session".
	override settingsNamePrefix = filterSettingsNamePrefix;

	constructor(options: FilterSortSettings<Board>) {
		super(options);
		const selectedFilters = this.readSavedSelectedFilters();
		this.isAnyLoopedBoardAvailable = this.allItems.some((item) => item instanceof BoardLoop);

		// Tags (used only)
		this.createFilter<number>({
			id: 1,
			selected: selectedFilters[1] as number | undefined,
			label: 'Tag',
			name: 'tag',
			isMultiSelect: true,
			getOptions: () => {
				return this.tagOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((tagId) => {
					return item.tags.includes(tagId);
				});
			},
		});

		// Creator
		this.createFilter<number>({
			id: 2,
			selected: selectedFilters[2] as number | undefined,
			label: 'Created by',
			name: 'created by',
			isMultiSelect: true,
			getOptions: () => {
				return this.ownerOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((ownerId) => {
					return (item?.ownerId || item?.createdById) === ownerId;
				});
			},
		});

		// Data source (used only)
		this.createFilter<string>({
			id: 3,
			selected: selectedFilters[3] as string | undefined,
			label: 'Data source',
			name: 'data source',
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => {
				return this.datasourceOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((sourceTypeKey) => {
					return item.specificSourceTypesKeys.includes(sourceTypeKey);
				});
			},
		});

		// Category type
		this.createFilter<DataboardCategory | 'all'>({
			id: 4,
			selected: selectedFilters[4] as DataboardCategory | undefined,
			label: 'Show',
			name: 'show',
			isMultiSelect: true,
			getOptions: () => {
				return this.categoryOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((categoryId) => {
					switch (categoryId) {
						case DataboardCategory.FAVORITE:
							return item.favorite;
						case DataboardCategory.VERIFIED:
							return isVerified(item.verificationInfo);
						case DataboardCategory.LOOPED:
							return item instanceof BoardLoop;
						default:
							return true;
					}
				});
			},
		});
	}

	override get viewOptions(): FilterOption<string>[] {
		return [
			{ id: 'table', text: 'List view', icon: 'List' },
			{ id: 'grid', text: 'Grid view', icon: 'Grid' },
		];
	}

	get tagOptions(): FilterOption<number>[] {
		const formattedOptions = [
			...this.mmBoardsService.assignedTagsForBoards,
		] as FilterOption<number>[];

		if (this.isAnyLoopedBoardAvailable) {
			// sum up boards and board loops used numbers
			[...this.mmBoardLoopsService.assignedTags].forEach((option) => {
				const existingOption = formattedOptions.find((item) => item.id === option.id);
				if (existingOption) {
					existingOption.used = (existingOption.used ?? 0) + option.used; // always set on tag options
				} else {
					formattedOptions.push(option);
				}
			});
		}

		if (formattedOptions.length) {
			formattedOptions.unshift({ id: 0, text: 'All' });
		}

		return formattedOptions;
	}

	get ownerOptions(): FilterOption<number>[] {
		const allItemCountByCreator: Record<number, number> = {};
		this.allItems.forEach((item) => {
			const createdById = item.createdById;
			if (!allItemCountByCreator[createdById]) {
				allItemCountByCreator[createdById] = 0;
			}
			allItemCountByCreator[createdById]++;
		});

		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByCreator);
	}

	@cached
	get datasourceOptions(): FilterOption<string>[] {
		// get how many boards each source is used on
		const sourceTypeArray: string[] = [];

		this.allItems.forEach((item) => {
			const sources: string[] = [];

			item.specificSourceTypesKeys.forEach((key: string) => {
				if (key) {
					sources.push(key);
				}
			});

			(item?.sourceTypeIdsMissingFromResolutionMessages || []).forEach(
				(missingType: number) => {
					if (missingType) {
						const missingSource = this.mmSourceTypesService.findModelById(missingType);

						if (missingSource) {
							sources.push(missingSource.key);
						}
					}
				},
			);

			sourceTypeArray.push(...uniq(sources));
		});

		const list = this.mmSourceTypesService.getSourceTypesListWithCount(sourceTypeArray);

		list.unshift({ id: 'all', text: 'All' });

		return list;
	}

	get categoryOptions(): FilterOption<DataboardCategory | 'all'>[] {
		const list: FilterOption<DataboardCategory | 'all'>[] = [
			{ id: 'all', text: 'All' },
			{
				id: DataboardCategory.FAVORITE,
				text: 'Favorite',
				icon: 'StarFill',
				color: 'var(--databox-yellow-500)',
			},
			{
				id: DataboardCategory.VERIFIED,
				text: 'Verified',
				icon: 'VerifiedFill',
			},
		];

		if (this.isAnyLoopedBoardAvailable) {
			list.push({ id: DataboardCategory.LOOPED, text: 'Looped' });
		}

		return list;
	}
}
